import React from "react";
import { Box, TextField, Button, Tooltip } from "@material-ui/core";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { MUTED } from "./tokens";
import { MAX_DISCOUNT_RATIO } from "./validators";
import MoneyField from "./MoneyField";
import PricingHistory from "./PricingHistory";
import { overOriginal, belowFloor } from "./pricingLines";
import { IssueBanner } from "./StepSection";

const PricingRow = ({
  line, classes, form, set, currencySym, money, readOnly, showErrors,
  onCreditPrice, onSubUnitPrice, onSubAmount,
}) => {
  const perUnit = line.source === "credit" || line.source === "unit";
  const unitValue = line.source === "credit"
    ? form.creditPrices[line.creditKey]
    : form.subUnitPrices[line.unitKey];

  return (
    <Box className={classes.prow}>
      <div className={classes.pcell}>
        <div className={classes.plbl}>{line.title}<span className={classes.src}>{line.tag}</span></div>
        {perUnit && (
          <div className={classes.unitWrap}>
            <span className={classes.unitSym}>{currencySym.trim()}</span>
            <MoneyField className={classes.unitInput}
              variant="standard" align="left" disabled={readOnly}
              style={{ width: 74 }} fullWidth={false}
              emphasis="secondary" placeholder="0.00"
              value={unitValue}
              error={showErrors && (
                !(Number(unitValue) > 0)
                // Also red when the row total is over its original or
                // under the 20% floor: the unit price is the only fix.
                || overOriginal(line)
                || belowFloor(line)
              )}
              onChange={(v) => line.source === "credit"
                ? onCreditPrice(line.creditKey, v)
                : onSubUnitPrice(line.unitKey, v)} />
            <span className={classes.unitNote}>
              {`per ${line.source === "credit" ? "credit" : line.unitName} × ${line.qty.toLocaleString("en-IN")}`}
            </span>
          </div>
        )}
      </div>
      <div className={classes.pcell}>
        {/* Locked where the catalog knows the list price. Credit lines
            have no catalog entry, so those stay editable. */}
        <MoneyField className={`${classes.input} ${classes.numInput} ${classes.origInput}`}
          emphasis="secondary" placeholder="—"
          lock={line.origLocked || readOnly}
          disabled={readOnly || line.origLocked}
          value={line.origLocked ? (line.originalAmount ?? "") : (form.originals[line.origKey] ?? "")}
          onChange={(v) => set({ originals: { ...form.originals, [line.origKey]: v } })} />
      </div>
      <div className={classes.pcell}>
        {perUnit ? (
          // Computed from unit price × quantity, still rendered as a
          // field so the Price column keeps one shape all the way down.
          <MoneyField className={`${classes.input} ${classes.numInput}`}
            emphasis="primary" disabled value={line.amount} onChange={() => {}}
            lock={readOnly} error={overOriginal(line) || belowFloor(line)} />
        ) : (
          <MoneyField className={`${classes.input} ${classes.numInput}`}
            emphasis="primary" disabled={readOnly} lock={readOnly}
            // Not gated behind `attempted` like the other pricing errors:
            // you only get here by typing a number, so say it at once.
            error={overOriginal(line) || belowFloor(line) || (showErrors && Number(line.amount) < 0)}
            value={form.subAmounts[line.id] ?? 0}
            onChange={(v) => onSubAmount(line.id, v)} />
        )}
        {overOriginal(line) && (
          <div className={classes.rowErr}>Max {money(line.originalAmount)}</div>
        )}
        {belowFloor(line) && (
          <div className={classes.rowErr}>Min {money(line.originalAmount * (1 - MAX_DISCOUNT_RATIO))} — max 20% off</div>
        )}
      </div>
      <div className={classes.pcell} />
    </Box>
  );
};

// Step 3 — the money. In edit mode this is read-only until the admin explicitly
// drafts a new pricing version, since published prices are immutable.
const PricingStep = ({
  classes, form, set, product, isEdit, readOnly, pricingDraft, activeVersion,
  lines, base, currencySym, money, showErrors, pricingErr, negFloor, saving,
  onCreditPrice, onSubUnitPrice, onSubAmount,
  issues, onBack, onStartDraft, onCancelDraft, onPublish,
}) => (
  <>
    {readOnly && (
      <div className={classes.lockBanner}>
        <LockOutlinedIcon style={{ fontSize: 14, color: MUTED, flex: "none" }} />
        <span>Prices are fixed on version {activeVersion}. Publish a new version to change them.</span>
      </div>
    )}
    <Box className={classes.ptable}>
      <Box className={classes.prowHead}>
        <div className={classes.pcell}>Line item</div>
        <div className={classes.pcellNum}>
          Original price ({currencySym.trim()})
          <Tooltip title="Catalog list price. You can charge less, not more.">
            <InfoOutlinedIcon className={classes.headInfo} />
          </Tooltip>
        </div>
        <div className={classes.pcellNum}>Price ({currencySym.trim()})</div>
        <div className={classes.pcell} />
      </Box>
      {lines.length === 0 && (
        <div className={classes.emptyLines}>Add an entitlement above — its priced line shows up here.</div>
      )}
      {lines.map((l) => (
        <PricingRow key={l.rowKey} line={l} classes={classes} form={form} set={set}
          currencySym={currencySym} money={money} readOnly={readOnly} showErrors={showErrors}
          onCreditPrice={onCreditPrice} onSubUnitPrice={onSubUnitPrice} onSubAmount={onSubAmount} />
      ))}
      <Box className={`${classes.sumRow} ${classes.sumRowTop}`}>
        <div className={classes.sumLabel}>
          Total price
          <Tooltip title="Added up from the line items above.">
            <InfoOutlinedIcon className={classes.headInfo} />
          </Tooltip>
        </div>
        <div className={classes.pcell}><div className={classes.sumVal}>{money(base)}</div></div>
        <div className={classes.pcell} />
      </Box>
      <Box className={classes.sumRow}>
        <div className={classes.sumLabel}>
          Never sell below
          <Tooltip title="The lowest this can go once any coupon is applied.">
            <InfoOutlinedIcon className={classes.headInfo} />
          </Tooltip>
        </div>
        <div className={classes.pcell}>
          <MoneyField className={`${classes.input} ${classes.numInput}`}
            symbol={currencySym.trim()} emphasis="primary"
            disabled={readOnly} lock={readOnly}
            value={form.minPrice} error={showErrors && (!!pricingErr.minPrice || negFloor)}
            onChange={(v) => set({ minPrice: v })} />
        </div>
        <div className={classes.pcell} />
      </Box>
    </Box>

    {isEdit && pricingDraft && (
      <div style={{ marginTop: 14 }}>
        <span className={classes.fieldLabel}>New price takes over on (optional)</span>
        <TextField className={classes.input} type="date" variant="outlined" size="small"
          InputLabelProps={{ shrink: true }}
          value={form.migrationDeadline || ""}
          onChange={(e) => set({ migrationDeadline: e.target.value })} />
        <div className={classes.hint}>Existing buyers keep their current price until this date.</div>
      </div>
    )}

    {!readOnly && <IssueBanner issues={issues} classes={classes} />}

    {isEdit ? (
      readOnly ? (
        <>
          <PricingHistory product={product} classes={classes} />
          <Box className={classes.continueRow}>
            <Button className={classes.continueBtn} variant="contained" onClick={onStartDraft}>
              Create new pricing version
            </Button>
          </Box>
        </>
      ) : (
        <Box className={classes.continueRow}>
          <Button onClick={onCancelDraft} disabled={saving}>Cancel</Button>
          <Button className={classes.continueBtn} variant="contained" onClick={onPublish} disabled={saving}>
            Publish version
          </Button>
        </Box>
      )
    ) : (
      <Box className={classes.continueRow}>
        <Button onClick={onBack}>Back</Button>
      </Box>
    )}
  </>
);

export default PricingStep;
